import type { Property } from "posthog-js"

import * as flatbuffers from "flatbuffers"

import type { EntityHandle } from "~/models/polyschema/EntityHandle"

import { EntityMimeTypeId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { AnyIntegerValueBuf, AnyStringValueBuf, BoolValueBuf, ConversationTurnValueBuf, EmailValueBuf, EntityHandleBuf, EntityHandleValueBuf, EntityMimeTypeBuf, EntityPropertySelectionBuf, EntityPropertySelectionEntryBuf, EntityPropertyStatusBuf, EntityPropertyTimestampBuf, ExifJsonStringValueBuf, FileAudioContainerMetadataValueBuf, FileDownloadSourceApplicationValueBuf, FileIdValueBuf, FileLinkOpenGraphMetadataValueBuf, FilePdfContainerMetadataValueBuf, FileThumbnailSetValueBuf, FileVideoContainerMetadataValueBuf, HashableTagValueBuf, MimeTypeValueBuf, NonEmptyStringValueBuf, NonNegativeIntegerValueBuf, PositiveFloatValueBuf, PositiveIntegerValueBuf, RoleUuidValueBuf, S3BlobUriValueBuf, S3ThumbnailUriValueBuf, SearchDefinitionValueBuf, SearchIndexingStatusValueBuf, SearchLocationsValueBuf, SearchPropertiesValueBuf, SearchResultValueBuf, Sha256HashValueBuf, SharedFolderIdValueBuf, SizeBytesValueBuf, UserIdValueBuf, UserInterfaceSettingsValueBuf, UserSidebarItemIdValueBuf, UtcTimestampMillisValueBuf, ViewModeSettingsValueBuf, WebUrlValueBuf } from "~/classes/generated/entity"
import { FileSha256HashBuf, RoleUuidBuf } from "~/classes/generated/file"

import { DatagramBufferBuilder } from "./builder"
import { addConversationTurnToBuf, parseConversationTurn } from "./conversation"
import { parseVector } from "./datagram"
import { addFileAudioContainerMetadataToBuf, addFileIdToBuf, addFileLinkOpenGraphMetadataToBuf, addFilePdfContainerMetadataToBuf, addFileThumbnailSetToBuf, addFileVideoContainerMetadataToBuf, parseFileAudioContainerMetadata, parseFileLinkOpenGraphMetadata, parseFilePdfContainerMetadata, parseFileThumbnailSet, parseFileVideoContainerMetadata, parseSearchIndexingStatus } from "./file"
import { addEntityIdToBuf, addUuidToBuf, parseFileId, parseFilePropertyId, parseSharedFolderId, parseUserId, parseUuidBuf } from "./id"
import { addViewModeSettingsToBuf, parseViewModeSettings } from "./interface"
import { addSearchDefinitionToBuf, parseSearchDefinition } from "./search"
import { addUserInterfaceSettingsToBuf, addUserSidebarItemIdToBuf, parseUserInterfaceSettings, parseUserSidebarItemId } from "./user"

export function parseEntityPropertySelectionEntry(
  buf: EntityPropertySelectionEntryBuf,
): [PropertyGroup, Partial<Record<Property, Property>>] {
  const groupId = buf.groupId() as PropertyGroup
  const groupStr = groupId.toString(16).padStart(8, "0")
  const ranges: Partial<Record<Property, Property>> = {}
  const starts = parseVector(i => buf.starts(i)!, buf.startsLength())
  const ends = parseVector(i => buf.ends(i)!, buf.endsLength())
  for (const [sBuf, eBuf] of zip(starts, ends)) {
    ranges[`${groupStr}-${parseUuidBuf(sBuf)}` as Property] = `${groupStr}-${parseUuidBuf(eBuf)}` as Property
  }
  return [groupId, ranges]
}

export function parseEntityPropertySelection(buf: EntityPropertySelectionBuf | null): EntityPropertySelection {
  if (!buf)
    return InterfaceSelection.nothing().inner()
  else if (buf.everything())
    return InterfaceSelection.everything().inner()

  const selection = InterfaceSelection.nothing().inner()
  const sections = parseVector(i => buf.sections(i)!, buf.sectionsLength()).map(parseEntityPropertySelectionEntry)
  for (const [groupId, ranges] of sections)
    selection.sections[groupId] = ranges
  return selection
}

export function addEntityPropertySelectionEntryToBuf(fbb: DatagramBufferBuilder, groupId: PropertyGroup, ranges: Record<string, string>) {
  const startOffsets = []
  for (const start of Object.keys(ranges)) {
    const uuid = partition(start, "-")[1]
    startOffsets.push(addUuidToBuf(fbb, uuid))
  }
  const starts = EntityPropertySelectionEntryBuf.createStartsVector(fbb, startOffsets)

  const endOffsets = []
  for (const end of Object.values(ranges)) {
    const uuid = partition(end, "-")[1]
    endOffsets.push(addUuidToBuf(fbb, uuid))
  }
  const ends = EntityPropertySelectionEntryBuf.createEndsVector(fbb, endOffsets)

  EntityPropertySelectionEntryBuf.startEntityPropertySelectionEntryBuf(fbb)
  EntityPropertySelectionEntryBuf.addGroupId(fbb, groupId as number)
  EntityPropertySelectionEntryBuf.addStarts(fbb, starts)
  EntityPropertySelectionEntryBuf.addEnds(fbb, ends)
  return EntityPropertySelectionEntryBuf.endEntityPropertySelectionEntryBuf(fbb)
}

export function addEntityPropertySelectionToBuf(
  fbb: DatagramBufferBuilder,
  selection: EntityPropertySelection,
) {
  const sectionsOffsets: number[] = []
  for (const [groupKey, ranges] of Object.entries(selection.sections))
    sectionsOffsets.push(addEntityPropertySelectionEntryToBuf(
      fbb,
      Number.parseInt(groupKey) as PropertyGroup,
      ranges!,
    ))
  let sections
  if (sectionsOffsets.length)
    sections = EntityPropertySelectionBuf.createSectionsVector(fbb, sectionsOffsets)

  EntityPropertySelectionBuf.startEntityPropertySelectionBuf(fbb)
  if (selection.everything)
    EntityPropertySelectionBuf.addEverything(fbb, true)
  if (sections)
    EntityPropertySelectionBuf.addSections(fbb, sections)
  return EntityPropertySelectionBuf.endEntityPropertySelectionBuf(fbb)
}

export function parseEntityPropertyStatus(
  buf: EntityPropertyStatusBuf,
): PropertyStatus {
  switch (buf) {
    case EntityPropertyStatusBuf.StatusCurrent: return PropertyStatus.StatusCurrent
    case EntityPropertyStatusBuf.StatusPending: return PropertyStatus.StatusPending
    case EntityPropertyStatusBuf.StatusVersioned: return PropertyStatus.StatusVersioned
    case EntityPropertyStatusBuf.StatusUnofficial: return PropertyStatus.StatusUnofficial
    case EntityPropertyStatusBuf.StatusLocal: return PropertyStatus.StatusLocal
  }
}

export function asEntityPropertyStatusBuf(
  status: PropertyStatus,
): EntityPropertyStatusBuf {
  switch (status) {
    case PropertyStatus.StatusCurrent: return EntityPropertyStatusBuf.StatusCurrent
    case PropertyStatus.StatusPending: return EntityPropertyStatusBuf.StatusPending
    case PropertyStatus.StatusVersioned: return EntityPropertyStatusBuf.StatusVersioned
    case PropertyStatus.StatusUnofficial: return EntityPropertyStatusBuf.StatusUnofficial
    case PropertyStatus.StatusLocal: return EntityPropertyStatusBuf.StatusLocal
    default: throw new Error(`Unknown status: ${status}`)
  }
}

export function parseEntityPropertyTimestampBuf(
  buf: EntityPropertyTimestampBuf,
): string {
  const uuid = parseUuidBuf(buf.id()!)
  return uuid.toString()
}

export function addEntityPropertyTimestampToBuf(fbb: DatagramBufferBuilder, timestamp: string) {
  const id = addUuidToBuf(fbb, timestamp)
  return EntityPropertyTimestampBuf.createEntityPropertyTimestampBuf(fbb, id)
}

export function parseEntityHandleBuf(
  buf: EntityHandleBuf,
): EntityHandle {
  const handle = buf.handle()!
  return { handle }
}

export function addEntityHandleToBuf(fbb: DatagramBufferBuilder, handle: EntityHandle) {
  const handleBuf = fbb.createString(handle.handle)
  return EntityHandleBuf.createEntityHandleBuf(fbb, handleBuf)
}

export function parseEntityPropertySchemaValue(
  schema: PropertySchema,
  buf: Uint8Array,
): EntityPropertyValue {
  const byteBuf = new flatbuffers.ByteBuffer(buf)
  switch (schema) {
    case PropertySchema.FileIdValue: {
      const value = FileIdValueBuf.getRootAsFileIdValueBuf(byteBuf).value()!
      const fileId = parseFileId(value)
      return { type: PropertySchema.FileIdValue, fileId }
    }
    case PropertySchema.UserIdValue: {
      const value = UserIdValueBuf.getRootAsUserIdValueBuf(byteBuf).value()!
      const userId = parseUserId(value)
      return { type: PropertySchema.UserIdValue, userId }
    }
    case PropertySchema.SharedFolderIdValue: {
      const value = SharedFolderIdValueBuf.getRootAsSharedFolderIdValueBuf(byteBuf).value()!
      const sharedFolderId = parseSharedFolderId(value)
      return { type: PropertySchema.SharedFolderIdValue, sharedFolderId }
    }
    case PropertySchema.UtcTimestampMillisValue: {
      const value = UtcTimestampMillisValueBuf.getRootAsUtcTimestampMillisValueBuf(byteBuf).value()!
      const utcTimestampMillis = Number(value)
      return { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis }
    }
    case PropertySchema.S3BlobUriValue: {
      const s3BlobUri = S3BlobUriValueBuf.getRootAsS3BlobUriValueBuf(byteBuf).value()!
      return { type: PropertySchema.S3BlobUriValue, s3BlobUri }
    }
    case PropertySchema.S3ThumbnailUriValue: {
      const s3ThumbnailUri = S3ThumbnailUriValueBuf.getRootAsS3ThumbnailUriValueBuf(byteBuf).value()!
      return { type: PropertySchema.S3ThumbnailUriValue, s3ThumbnailUri }
    }
    case PropertySchema.WebUrlValue: {
      const url = WebUrlValueBuf.getRootAsWebUrlValueBuf(byteBuf).value()!
      return { type: PropertySchema.WebUrlValue, url }
    }
    case PropertySchema.NonEmptyStringValue: {
      const nonEmptyString = NonEmptyStringValueBuf.getRootAsNonEmptyStringValueBuf(byteBuf).value()!
      return { type: PropertySchema.NonEmptyStringValue, nonEmptyString }
    }
    case PropertySchema.AnyStringValue: {
      const anyString = AnyStringValueBuf.getRootAsAnyStringValueBuf(byteBuf).value() ?? ""
      return { type: PropertySchema.AnyStringValue, anyString }
    }
    case PropertySchema.HashableTagValue: {
      const hashableTag = HashableTagValueBuf.getRootAsHashableTagValueBuf(byteBuf).value()!
      return { type: PropertySchema.HashableTagValue, hashableTag }
    }
    case PropertySchema.EmailValue: {
      const email = EmailValueBuf.getRootAsEmailValueBuf(byteBuf).value()!
      return { type: PropertySchema.EmailValue, email }
    }
    case PropertySchema.EntityHandleValue: {
      const entityHandleBuf = EntityHandleValueBuf.getRootAsEntityHandleValueBuf(byteBuf).value()!
      const entityHandle = parseEntityHandleBuf(entityHandleBuf)
      return { type: PropertySchema.EntityHandleValue, entityHandle }
    }
    case PropertySchema.BoolValue: {
      const bool = BoolValueBuf.getRootAsBoolValueBuf(byteBuf).value()!
      return { type: PropertySchema.BoolValue, bool }
    }
    case PropertySchema.PositiveIntegerValue: {
      const value = PositiveIntegerValueBuf.getRootAsPositiveIntegerValueBuf(byteBuf).value()!
      const positiveInteger = Number(value)
      return { type: PropertySchema.PositiveIntegerValue, positiveInteger }
    }
    case PropertySchema.PositiveFloatValue: {
      const PositiveFloat = PositiveFloatValueBuf.getRootAsPositiveFloatValueBuf(byteBuf).value()!
      return { type: PropertySchema.PositiveFloatValue, positiveFloat: PositiveFloat }
    }
    case PropertySchema.SizeBytesValue: {
      const value = SizeBytesValueBuf.getRootAsSizeBytesValueBuf(byteBuf).value()!
      const sizeBytes = Number(value)
      return { type: PropertySchema.SizeBytesValue, sizeBytes }
    }
    case PropertySchema.NonNegativeIntegerValue: {
      const value = NonNegativeIntegerValueBuf.getRootAsNonNegativeIntegerValueBuf(byteBuf).value()!
      const nonNegativeInteger = Number(value)
      return { type: PropertySchema.NonNegativeIntegerValue, nonNegativeInteger }
    }
    case PropertySchema.AnyIntegerValue: {
      const value = AnyIntegerValueBuf.getRootAsAnyIntegerValueBuf(byteBuf).value()!
      const anyInteger = Number(value)
      return { type: PropertySchema.AnyIntegerValue, anyInteger }
    }
    case PropertySchema.MimeTypeValue: {
      const root = MimeTypeValueBuf.getRootAsMimeTypeValueBuf(byteBuf).value()!
      const value = root.value()
      const unknown = root.unknown()
      let mimeType: EntityMimeType
      if (value === EntityMimeTypeId.Unknown && isDefined(unknown)) {
        mimeType = { value, unknown }
      }
      else if (value !== EntityMimeTypeId.Unknown) {
        mimeType = { value }
      }
      else {
        console.error("Invalid mime type", root)
        throw new Error("Cannot parse mime type")
      }
      return { type: PropertySchema.MimeTypeValue, mimeType }
    }
    case PropertySchema.ExifJsonStringValue: {
      const exifString = ExifJsonStringValueBuf.getRootAsExifJsonStringValueBuf(byteBuf).value()!
      const exif = JSON.parse(exifString)
      return { type: PropertySchema.ExifJsonStringValue, exif }
    }
    case PropertySchema.RoleUuidValue: {
      const value = RoleUuidValueBuf.getRootAsRoleUuidValueBuf(byteBuf).value()!
      const key = parseUuidBuf(value.role()!)
      return { type: PropertySchema.RoleUuidValue, role: key.toString() }
    }
    case PropertySchema.UserSidebarItemIdValue: {
      const value = UserSidebarItemIdValueBuf.getRootAsUserSidebarItemIdValueBuf(byteBuf).value()!
      const UserSidebarItemId = parseUserSidebarItemId(value)
      return { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId }
    }
    case PropertySchema.FolderResponseDetailsValue: {
      console.warn(
        "Cache should exclude this property in client response",
        PropertySchema.FolderResponseDetailsValue,
      )
      return { type: PropertySchema.FolderResponseDetailsValue, byteBuf }
    }
    case PropertySchema.EntityPropertySelectionValue: {
      console.warn(
        "Cache should exclude this property in client response",
        PropertySchema.EntityPropertySelectionValue,
      )
      return { type: PropertySchema.EntityPropertySelectionValue, byteBuf }
    }
    case PropertySchema.Sha256HashValue: {
      const buf = Sha256HashValueBuf.getRootAsSha256HashValueBuf(byteBuf).value()!
      const hash = new Uint8Array([
        buf.hash0(),
        buf.hash1(),
        buf.hash2(),
        buf.hash3(),
        buf.hash4(),
        buf.hash5(),
        buf.hash6(),
        buf.hash7(),
        buf.hash8(),
        buf.hash9(),
        buf.hash10(),
        buf.hash11(),
        buf.hash12(),
        buf.hash13(),
        buf.hash14(),
        buf.hash15(),
        buf.hash16(),
        buf.hash17(),
        buf.hash18(),
        buf.hash19(),
        buf.hash20(),
        buf.hash21(),
        buf.hash22(),
        buf.hash23(),
        buf.hash24(),
        buf.hash25(),
        buf.hash26(),
        buf.hash27(),
        buf.hash28(),
        buf.hash29(),
        buf.hash30(),
        buf.hash31(),
      ])
      return { type: PropertySchema.Sha256HashValue, hash }
    }
    case PropertySchema.SearchIndexingStatusValue: {
      const buf = SearchIndexingStatusValueBuf.getRootAsSearchIndexingStatusValueBuf(byteBuf).value()!
      const status = parseSearchIndexingStatus(buf)
      return { type: PropertySchema.SearchIndexingStatusValue, status }
    }
    case PropertySchema.FileThumbnailSetValue: {
      const setBuf = FileThumbnailSetValueBuf.getRootAsFileThumbnailSetValueBuf(byteBuf).value()!
      const set = parseFileThumbnailSet(setBuf)
      return { type: PropertySchema.FileThumbnailSetValue, set }
    }
    case PropertySchema.FileAudioContainerMetadataValue: {
      const setBuf = FileAudioContainerMetadataValueBuf.getRootAsFileAudioContainerMetadataValueBuf(byteBuf).value()!
      const metadata = parseFileAudioContainerMetadata(setBuf)
      return { type: PropertySchema.FileAudioContainerMetadataValue, metadata }
    }
    case PropertySchema.FileVideoContainerMetadataValue: {
      const setBuf = FileVideoContainerMetadataValueBuf.getRootAsFileVideoContainerMetadataValueBuf(byteBuf).value()!
      const metadata = parseFileVideoContainerMetadata(setBuf)
      return { type: PropertySchema.FileVideoContainerMetadataValue, metadata }
    }
    case PropertySchema.FilePdfContainerMetadataValue: {
      const setBuf = FilePdfContainerMetadataValueBuf.getRootAsFilePdfContainerMetadataValueBuf(byteBuf).value()!
      const metadata = parseFilePdfContainerMetadata(setBuf)
      return { type: PropertySchema.FilePdfContainerMetadataValue, metadata }
    }
    case PropertySchema.FileLinkOpenGraphMetadataValue: {
      const setBuf = FileLinkOpenGraphMetadataValueBuf.getRootAsFileLinkOpenGraphMetadataValueBuf(byteBuf).value()!
      const metadata = parseFileLinkOpenGraphMetadata(setBuf)
      return { type: PropertySchema.FileLinkOpenGraphMetadataValue, metadata }
    }
    case PropertySchema.ConversationTurnValue: {
      const setBuf = ConversationTurnValueBuf.getRootAsConversationTurnValueBuf(byteBuf).value()!
      const turn = parseConversationTurn(setBuf)
      return { type: PropertySchema.ConversationTurnValue, turn }
    }
    case PropertySchema.SearchLocationsValue: {
      const locationBuf = SearchLocationsValueBuf.getRootAsSearchLocationsValueBuf(byteBuf)
      const locations = parseVector(i => parseFileId(locationBuf.value(i)!), locationBuf.valueLength())
      return { type: PropertySchema.SearchLocationsValue, locations }
    }
    case PropertySchema.SearchResultValue: {
      const resultBuf = SearchResultValueBuf.getRootAsSearchResultValueBuf(byteBuf)
      const searchResultBuf = resultBuf.value()!
      return {
        type: PropertySchema.SearchResultValue,
        result: { fileId: parseFileId(searchResultBuf.fileId()!), score: searchResultBuf.score() },
      }
    }
    case PropertySchema.SearchPropertiesValue: {
      const propertiesBuf = SearchPropertiesValueBuf.getRootAsSearchPropertiesValueBuf(byteBuf)
      const properties = parseVector(i => parseFilePropertyId(propertiesBuf.value(i)!), propertiesBuf.valueLength())
      return { type: PropertySchema.SearchPropertiesValue, properties }
    }
    case PropertySchema.ViewModeSettingsValue: {
      const settingsBuf = ViewModeSettingsValueBuf.getRootAsViewModeSettingsValueBuf(byteBuf)
      const settings = parseViewModeSettings(settingsBuf.value()!)
      return { type: PropertySchema.ViewModeSettingsValue, settings }
    }
    case PropertySchema.UserInterfaceSettingsValue: {
      const interfaceBuf = UserInterfaceSettingsValueBuf.getRootAsUserInterfaceSettingsValueBuf(byteBuf)
      const userInterfaceSettings = parseUserInterfaceSettings(interfaceBuf.value()!)
      return { type: PropertySchema.UserInterfaceSettingsValue, userInterfaceSettings }
    }
    case PropertySchema.SearchDefinitionValue: {
      const definitionBuf = SearchDefinitionValueBuf.getRootAsSearchDefinitionValueBuf(byteBuf)
      const definition = parseSearchDefinition(definitionBuf.value()!)
      return { type: PropertySchema.SearchDefinitionValue, definition }
    }
    case PropertySchema.FileDownloadSourceApplicationValue: {
      const applicationBuf = FileDownloadSourceApplicationValueBuf.getRootAsFileDownloadSourceApplicationValueBuf(byteBuf)
      const sourceApplication = applicationBuf.value()!
      return { type: PropertySchema.FileDownloadSourceApplicationValue, sourceApplication }
    }
    default: {
      return { type: null, unknownType: schema, unknownByteBuf: buf }
    }
  }
}

export function addEntityPropertyValueToBuf(
  fbb: DatagramBufferBuilder,
  value: EntityPropertyValue,
) {
  const jsonEncodedValue = JSON.stringify(value)
  if (fbb.values.has(jsonEncodedValue))
    return fbb.values.get(jsonEncodedValue)!

  const schemaFbb = new DatagramBufferBuilder(64)

  if (value.type === PropertySchema.FileIdValue) {
    const fileId = addEntityIdToBuf(schemaFbb, value.fileId)
    const buf = FileIdValueBuf.createFileIdValueBuf(schemaFbb, fileId)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.UserIdValue) {
    const userId = addEntityIdToBuf(schemaFbb, value.userId)
    const buf = UserIdValueBuf.createUserIdValueBuf(schemaFbb, userId)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.SharedFolderIdValue) {
    const sharedFolderId = addEntityIdToBuf(schemaFbb, value.sharedFolderId)
    const buf = SharedFolderIdValueBuf.createSharedFolderIdValueBuf(schemaFbb, sharedFolderId)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.UtcTimestampMillisValue) {
    const buf = UtcTimestampMillisValueBuf.createUtcTimestampMillisValueBuf(schemaFbb, BigInt(value.utcTimestampMillis))
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.S3BlobUriValue) {
    const s3BlobUri = schemaFbb.createString(value.s3BlobUri)
    const buf = S3BlobUriValueBuf.createS3BlobUriValueBuf(schemaFbb, s3BlobUri)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.S3ThumbnailUriValue) {
    const s3ThumbnailUri = schemaFbb.createString(value.s3ThumbnailUri)
    const buf = S3ThumbnailUriValueBuf.createS3ThumbnailUriValueBuf(schemaFbb, s3ThumbnailUri)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.WebUrlValue) {
    const webUrl = schemaFbb.createString(value.url)
    const buf = WebUrlValueBuf.createWebUrlValueBuf(schemaFbb, webUrl)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.NonEmptyStringValue) {
    const nonEmptyString = schemaFbb.createString(value.nonEmptyString)
    const buf = NonEmptyStringValueBuf.createNonEmptyStringValueBuf(schemaFbb, nonEmptyString)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.AnyStringValue) {
    const anyString = schemaFbb.createString(value.anyString)
    const buf = AnyStringValueBuf.createAnyStringValueBuf(schemaFbb, anyString)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.HashableTagValue) {
    const hashableTag = schemaFbb.createString(value.hashableTag)
    const buf = HashableTagValueBuf.createHashableTagValueBuf(schemaFbb, hashableTag)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.EmailValue) {
    const email = schemaFbb.createString(value.email)
    const buf = EmailValueBuf.createEmailValueBuf(schemaFbb, email)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.EntityHandleValue) {
    const entityHandle = addEntityHandleToBuf(schemaFbb, value.entityHandle)
    const buf = EntityHandleValueBuf.createEntityHandleValueBuf(schemaFbb, entityHandle)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.BoolValue) {
    const buf = BoolValueBuf.createBoolValueBuf(schemaFbb, value.bool)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.PositiveIntegerValue) {
    const buf = PositiveIntegerValueBuf.createPositiveIntegerValueBuf(schemaFbb, BigInt(value.positiveInteger))
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.NonNegativeIntegerValue) {
    const buf = NonNegativeIntegerValueBuf.createNonNegativeIntegerValueBuf(schemaFbb, BigInt(value.nonNegativeInteger))
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.PositiveFloatValue) {
    const buf = PositiveFloatValueBuf.createPositiveFloatValueBuf(schemaFbb, value.positiveFloat)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.SizeBytesValue) {
    const buf = SizeBytesValueBuf.createSizeBytesValueBuf(schemaFbb, BigInt(value.sizeBytes))
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.AnyIntegerValue) {
    const buf = AnyIntegerValueBuf.createAnyIntegerValueBuf(schemaFbb, BigInt(value.anyInteger))
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.MimeTypeValue) {
    let unknown
    if (value.mimeType.value === EntityMimeTypeId.Unknown)
      unknown = schemaFbb.createString(value.mimeType.unknown)
    EntityMimeTypeBuf.startEntityMimeTypeBuf(schemaFbb)
    EntityMimeTypeBuf.addValue(schemaFbb, value.mimeType.value)
    if (unknown)
      EntityMimeTypeBuf.addUnknown(schemaFbb, unknown)
    const mimeType = EntityMimeTypeBuf.endEntityMimeTypeBuf(schemaFbb)
    const buf = MimeTypeValueBuf.createMimeTypeValueBuf(schemaFbb, mimeType)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.Sha256HashValue) {
    const buf = FileSha256HashBuf.createFileSha256HashBuf(schemaFbb, value.hash[0], value.hash[1], value.hash[2], value.hash[3], value.hash[4], value.hash[5], value.hash[6], value.hash[7], value.hash[8], value.hash[9], value.hash[10], value.hash[11], value.hash[12], value.hash[13], value.hash[14], value.hash[15], value.hash[16], value.hash[17], value.hash[18], value.hash[19], value.hash[20], value.hash[21], value.hash[22], value.hash[23], value.hash[24], value.hash[25], value.hash[26], value.hash[27], value.hash[28], value.hash[29], value.hash[30], value.hash[31])
    const sha = Sha256HashValueBuf.createSha256HashValueBuf(schemaFbb, buf)
    schemaFbb.finish(sha)
  }
  else if (value.type === PropertySchema.RoleUuidValue) {
    const inner = addUuidToBuf(schemaFbb, value.role)
    const role = RoleUuidBuf.createRoleUuidBuf(schemaFbb, inner)
    const buf = RoleUuidValueBuf.createRoleUuidValueBuf(schemaFbb, role)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.UserSidebarItemIdValue) {
    const item = addUserSidebarItemIdToBuf(schemaFbb, value.UserSidebarItemId)
    const buf = UserSidebarItemIdValueBuf.createUserSidebarItemIdValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }
  else if (value.type === PropertySchema.FileThumbnailSetValue) {
    const item = addFileThumbnailSetToBuf(schemaFbb, value.set)
    const buf = FileThumbnailSetValueBuf.createFileThumbnailSetValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.FileAudioContainerMetadataValue) {
    const item = addFileAudioContainerMetadataToBuf(schemaFbb, value.metadata)
    const buf = FileAudioContainerMetadataValueBuf.createFileAudioContainerMetadataValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.FileVideoContainerMetadataValue) {
    const item = addFileVideoContainerMetadataToBuf(schemaFbb, value.metadata)
    const buf = FileVideoContainerMetadataValueBuf.createFileVideoContainerMetadataValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.FilePdfContainerMetadataValue) {
    const item = addFilePdfContainerMetadataToBuf(schemaFbb, value.metadata)
    const buf = FilePdfContainerMetadataValueBuf.createFilePdfContainerMetadataValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.FileLinkOpenGraphMetadataValue) {
    const item = addFileLinkOpenGraphMetadataToBuf(schemaFbb, value.metadata)
    const buf = FileLinkOpenGraphMetadataValueBuf.createFileLinkOpenGraphMetadataValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.ConversationTurnValue) {
    const item = addConversationTurnToBuf(schemaFbb, value.turn)
    const buf = ConversationTurnValueBuf.createConversationTurnValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.ViewModeSettingsValue) {
    const item = addViewModeSettingsToBuf(schemaFbb, value.settings)
    const buf = ViewModeSettingsValueBuf.createViewModeSettingsValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.UserInterfaceSettingsValue) {
    const item = addUserInterfaceSettingsToBuf(schemaFbb, value.userInterfaceSettings)
    const buf = UserInterfaceSettingsValueBuf.createUserInterfaceSettingsValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.SearchDefinitionValue) {
    const item = addSearchDefinitionToBuf(schemaFbb, value.definition)
    const buf = SearchDefinitionValueBuf.createSearchDefinitionValueBuf(schemaFbb, item)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.SearchLocationsValue) {
    const locationBuffers = value.locations.map(fileId => addFileIdToBuf(schemaFbb, fileId))
    const locations = SearchLocationsValueBuf.createValueVector(schemaFbb, locationBuffers)
    const buf = SearchLocationsValueBuf.createSearchLocationsValueBuf(schemaFbb, locations)
    schemaFbb.finish(buf)
  }

  else if (value.type === PropertySchema.FileDownloadSourceApplicationValue) {
    const buf = FileDownloadSourceApplicationValueBuf.createFileDownloadSourceApplicationValueBuf(schemaFbb, value.sourceApplication)
    schemaFbb.finish(buf)
  }

  else if (value.type === null && "unknownType" in value) {
    const byteArray = value.unknownByteBuf
    const offset = fbb.createByteVector(byteArray)
    fbb.values.set(jsonEncodedValue, offset)
    return offset
  }

  else
    throw new Error(`Unknown/invalid schema type: ${value}`)

  const byteArray = schemaFbb.asUint8Array()
  const offset = fbb.createByteVector(byteArray)
  fbb.values.set(jsonEncodedValue, offset)
  return offset
}
